import React, { useState } from "react";
import {
  Dialog,
  Flex,
  Card,
  Button,
  Text,
  TextArea,
  Select,
  Box,
  Separator,
  Switch,
} from "@radix-ui/themes";
import {
  AiOutlineBug,
  AiOutlineClose,
  AiOutlineSend,
  AiOutlineAudio,
  AiOutlineRobot,
  AiOutlineLoading,
  AiOutlineVideoCamera,
  AiOutlineClockCircle,
  AiOutlineQuestionCircle,
  AiOutlineKey,
} from "react-icons/ai";

const issueOptions = [
  {
    value: "voice-not-recognizing",
    label: "My voice is not being recognized",
    icon: <AiOutlineAudio size={20} />,
  },
  {
    value: "ai-not-speaking",
    label: "AI is not speaking",
    icon: <AiOutlineRobot size={20} />,
  },
  {
    value: "typing-not-working",
    label: "Typing is not working",
    icon: <AiOutlineKey size={20} />,
  },
  {
    value: "loading-issue",
    label: "Loading issues",
    icon: <AiOutlineLoading size={20} />,
  },
  {
    value: "video-upload-failed",
    label: "Unable to upload recorded video",
    icon: <AiOutlineVideoCamera size={20} />,
  },
  {
    value: "result-generation-slow",
    label: "Taking too much time for result generation",
    icon: <AiOutlineClockCircle size={20} />,
  },
  {
    value: "other",
    label: "Other issue",
    icon: <AiOutlineQuestionCircle size={20} />,
  },
];

const issueInstructions = {
  "voice-not-recognizing":
    "Try checking your microphone connection and permissions. Refresh the page and allow microphone access. If the problem persists, try using a different browser.",
  "ai-not-speaking":
    "Ensure your device's sound is not muted and your volume is up. Try refreshing the page. If the issue continues, try a different browser.",
  "typing-not-working":
    "Make sure your keyboard is connected and working in other applications. Try refreshing the page. If you still can't type, check if the interview allows typing.",
  "loading-issue":
    "Check your internet connection. Try refreshing the page or clearing your browser cache. If the issue persists, try a different browser or device.",
  "video-upload-failed":
    "Ensure your internet connection is stable. Try re-uploading the video. If the problem continues, check your file size and format.",
  "result-generation-slow":
    "Result generation may take a few minutes. Please wait and avoid refreshing the page. If it takes too long, try again after some time.",
};

const IssueReportPopup = ({
  open,
  onOpenChange,
  theme,
  changeSttService,
  changeTtsService,
}) => {
  const [selectedIssue, setSelectedIssue] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [backupVoice, setBackupVoice] = useState(false);
  const [backupSpeaker, setBackupSpeaker] = useState(false);

  const handleSubmit = async () => {
    if (!selectedIssue) {
      alert("Please select an issue type");
      return;
    }
    setIsSubmitting(true);
    try {
      // Here you would typically send the issue report to your backend
      console.log("Issue Report:", {
        issueType: selectedIssue,
        description: description,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      });
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSelectedIssue("");
      setDescription("");
      onOpenChange(false);
      alert(
        "Issue report submitted successfully. We'll look into it right away!"
      );
    } catch (error) {
      console.error("Error submitting issue report:", error);
      alert("Failed to submit issue report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedIssue("");
    setDescription("");
    onOpenChange(false);
  };

  // Styling helpers
  const dialogBg = theme === "dark" ? "#18181b" : "#fff";
  const dialogText = theme === "dark" ? "#fff" : "#222";
  const dropdownBg = theme === "dark" ? "#232325" : "#fff";
  const dropdownBorder = theme === "dark" ? "#3f3f46" : "#d1d5db";
  const dropdownHover = theme === "dark" ? "#27272a" : "#f3f4f6";
  const dropdownActive = theme === "dark" ? "#33334d" : "#e5e7eb";
  const iconColor = theme === "dark" ? "#FF6B00" : "#FF6B00";

  const clickButtonById = (id) => {
    console.log(id, "id");
    const button = document.getElementById(id);
    if (button) {
      button.click();
    } else {
      console.warn(`Button with id "${id}" not found.`);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        style={{
          maxWidth: 600,
          width: "90vw",
          maxHeight: "80vh",
          overflow: "hidden",
          background: dialogBg,
          color: dialogText,
          borderRadius: 18,
          boxShadow:
            theme === "dark"
              ? "0 4px 32px 0 rgba(0,0,0,0.7)"
              : "0 4px 32px 0 rgba(0,0,0,0.12)",
        }}
      >
        <Dialog.Title
          style={{
            color: iconColor,
            fontSize: "1.5rem",
            fontWeight: 600,
            marginBottom: "1rem",
          }}
        >
          <Flex align="center" gap="3">
            <AiOutlineBug size={24} style={{ color: iconColor }} />
            <span style={{ color: dialogText }}>Solve & Report an Issue</span>
          </Flex>
        </Dialog.Title>

        <Dialog.Description
          style={{
            color: theme === "dark" ? "#a1a1aa" : "#6b7280",
            marginBottom: "1.5rem",
            fontSize: "0.95rem",
          }}
        >
          Help us improve your experience by reporting any issues you encounter
          during the interview.
        </Dialog.Description>

        <Box style={{ marginBottom: "1.5rem" }}>
          <Text
            as="label"
            size="2"
            weight="500"
            style={{
              color: dialogText,
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            What issue are you experiencing? *
          </Text>

          <Select.Root
            value={selectedIssue}
            onValueChange={setSelectedIssue}
            size="3"
          >
            <Select.Trigger
              placeholder="Select an issue type"
              style={{
                width: "100%",
                background: dropdownBg,
                border: `1px solid ${dropdownBorder}`,
                color: theme === "dark" ? "#a1a1aa" : "#6b7280",
                borderRadius: 12,
                fontSize: 17,
                minHeight: 48,
              }}
              className="placeholder:text-white"
            />
            <Select.Content
              style={{
                background: dropdownBg,
                border: `1px solid ${dropdownBorder}`,
                borderRadius: 12,
                boxShadow:
                  theme === "dark"
                    ? "0 4px 24px 0 rgba(0,0,0,0.7)"
                    : "0 4px 24px 0 rgba(0,0,0,0.12)",
                padding: 2,
                color: theme === "dark" ? "#a1a1aa" : "#6b7280",
              }}
              className="no-select-check"
            >
              {issueOptions.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  style={{
                    color: dialogText,
                    borderRadius: 10,
                    padding: "12px 10px 12px 0",
                    margin: "2px 0",
                    background:
                      selectedIssue === option.value
                        ? dropdownActive
                        : "transparent",
                    fontWeight: selectedIssue === option.value ? 600 : 400,
                    fontSize: 16,
                    display: "flex",
                    alignItems: "center",
                    transition: "background 0.2s",
                  }}
                >
                  <Flex align="center" gap="3" style={{ width: "100%" }}>
                    <Box style={{ color: iconColor }}>{option.icon}</Box>
                    <Flex
                      direction="column"
                      align="start"
                      gap="1"
                      style={{ width: "100%" }}
                    >
                      <Text
                        weight={selectedIssue === option.value ? "600" : "500"}
                        size="2"
                      >
                        {option.label}
                      </Text>
                      {/* <Text
                        size="1"
                        style={{
                          color: theme === "dark" ? "#a1a1aa" : "#6b7280",
                        }}
                      >
                        {option.description}
                      </Text> */}
                    </Flex>
                  </Flex>
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>

        {/* Show solution/instruction for selected issue (except 'other') */}
        {selectedIssue && selectedIssue !== "other" && (
          <Card
            style={{
              background: theme === "dark" ? "#232325" : "#f9fafb",
              color: dialogText,
              borderRadius: 12,
              marginBottom: 24,
              padding: 18,
              fontSize: 16,
              boxShadow:
                theme === "dark"
                  ? "0 2px 8px 0 rgba(0,0,0,0.32)"
                  : "0 2px 8px 0 rgba(0,0,0,0.08)",
            }}
          >
            <Text weight="500" style={{ color: iconColor, fontSize: 17 }}>
              Solution:
            </Text>
            <br />
            <Text
              style={{
                color: theme === "dark" ? "#a1a1aa" : "#6b7280",
                fontSize: 15,
              }}
            >
              {issueInstructions[selectedIssue]}
            </Text>
            {/* Backup service switches for relevant issues */}
            {selectedIssue === "voice-not-recognizing" && (
              <Flex align="center" gap="3" mt="3" style={{ marginTop: 18 }}>
                <Switch
                  checked={backupVoice}
                  onCheckedChange={(checked) => {
                    setBackupVoice(checked);
                    console.log("Backup voice service:", checked);
                    if (checked && changeSttService) {
                      changeSttService("browser-stt");
                      setTimeout(() => {
                        clickButtonById("mute-btn-chrome");
                      }, 1000);
                    } else if (!checked && changeSttService) {
                      changeSttService("azure-stt");
                    }
                  }}
                  style={{ marginRight: 8 }}
                />
                <Text style={{ color: dialogText, fontSize: 15 }}>
                  Turn on backup voice service
                </Text>
              </Flex>
            )}
            {selectedIssue === "ai-not-speaking" && (
              <Flex align="center" gap="3" mt="3" style={{ marginTop: 18 }}>
                <Switch
                  checked={backupSpeaker}
                  onCheckedChange={(checked) => {
                    setBackupSpeaker(checked);
                    console.log("Backup speaker service:", checked);
                    if (checked && changeTtsService) {
                      changeTtsService("browser-tts");
                    } else if (!checked && changeTtsService) {
                      changeTtsService("azure-tts");
                    }
                  }}
                  style={{ marginRight: 8 }}
                />
                <Text style={{ color: dialogText, fontSize: 15 }}>
                  Turn on backup speaker service
                </Text>
              </Flex>
            )}
          </Card>
        )}

        {/* Only show textarea and submit for 'other' */}
        {selectedIssue === "other" && (
          <Box style={{ marginBottom: "1.5rem" }}>
            <Text
              as="label"
              size="2"
              weight="500"
              style={{
                color: dialogText,
                marginBottom: "0.5rem",
                display: "block",
              }}
            >
              Please describe your issue
            </Text>
            <TextArea
              placeholder="Please provide any additional details about the issue you're experiencing..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: "100%",
                minHeight: 100,
                background: theme === "dark" ? "#232325" : "#fff",
                border: `1px solid ${dropdownBorder}`,
                color: dialogText,
                resize: "vertical",
                borderRadius: 10,
              }}
            />
          </Box>
        )}

        <Separator size="4" style={{ margin: "1rem 0" }} />

        <Flex gap="3" justify="end">
          <Button
            variant="soft"
            color="gray"
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              background: theme === "dark" ? "#3f3f46" : "#f3f4f6",
              color: dialogText,
            }}
          >
            <AiOutlineClose size={16} />
            Cancel
          </Button>
          {selectedIssue === "other" ? (
            <Button
              onClick={handleSubmit}
              disabled={!description || isSubmitting}
              style={{
                background: "#FF6B00",
                color: "#fff",
                opacity: !description || isSubmitting ? 0.5 : 1,
              }}
            >
              <AiOutlineSend size={16} />
              {isSubmitting ? "Submitting..." : "Submit Report"}
            </Button>
          ) : selectedIssue ? (
            <Button
              onClick={handleClose}
              style={{
                background: "#FF6B00",
                color: "#fff",
              }}
            >
              Got it
            </Button>
          ) : null}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default IssueReportPopup;
